import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Empty, Progress, Spin, Table, Tag, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  ManpowerSummaryRow,
  fmtINR,
  fmtINRCompact,
  fmtQty,
} from "./manpowerShared";

// Per-labour-item task drill-down: for ONE register row (a PRW norm item or an
// Others labour type/category), its cost broken down phase → activity → task
// (three nested ranked tables). Identity travels as source + conditional ids —
// the backend treats an absent Others id as IS NULL, so the "Unlinked labour"
// row drills down too. Respects the page's current phase/activity filters.

interface BreakdownRow {
  phaseId: number;
  phaseName: string;
  activityId: number;
  activityName: string;
  taskId: number;
  taskName: string;
  taskSequenceId?: string;
  totalQuantity?: string;
  totalCost?: string;
}

interface TaskRow {
  key: number;
  rank: number;
  name: string;
  sequenceId?: string;
  qty: number;
  cost: number;
  share: number; // % of parent activity cost
}

interface ActivityRow {
  key: number;
  rank: number;
  name: string;
  qty: number;
  cost: number;
  share: number; // % of parent phase cost
  tasks: TaskRow[];
}

interface PhaseRow {
  key: number;
  rank: number;
  name: string;
  qty: number;
  cost: number;
  share: number; // % of the item's total cost
  activities: ActivityRow[];
}

const costCell = (cost: number) => (
  <Tooltip title={fmtINR(cost)}>
    <span
      style={{
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        cursor: "default",
      }}
    >
      {fmtINRCompact(cost)}
    </span>
  </Tooltip>
);

const qtyCell = (qty: number) => (
  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(qty)}</span>
);

const shareCell = (share: number) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <Progress
      percent={Math.min(100, Math.round(share * 10) / 10)}
      size="small"
      strokeWidth={4}
      showInfo={false}
      strokeColor="#1677ff"
      style={{ flex: 1, minWidth: 60, margin: 0 }}
    />
    <span
      style={{
        fontSize: 12,
        color: "#595959",
        fontVariantNumeric: "tabular-nums",
        width: 42,
        textAlign: "right",
      }}
    >
      {share.toFixed(1)}%
    </span>
  </div>
);

const rankCell = (rank: number) => (
  <span style={{ color: "#8c8c8c" }}>{rank}</span>
);

const ManpowerTaskBreakdownDrawer: React.FC<{
  open: boolean;
  projectId?: number | string;
  row?: ManpowerSummaryRow | null;
  phaseId?: number | string;
  activityId?: number | string;
  onClose: () => void;
  // Render target override — the page passes its own container so the drawer
  // stays visible while the report is in browser fullscreen.
  getContainer?: () => HTMLElement;
}> = ({ open, projectId, row, phaseId, activityId, onClose, getContainer }) => {
  const wbsName = useWbsName();
  const [rows, setRows] = useState<BreakdownRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId || !row) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setRows([]);
        const response = await threeMReportApis.get3MManpowerTaskBreakdown(
          projectId,
          {
            source: row.source,
            manpowerNormId: row.manpowerNormId,
            laborTypeId: row.laborTypeId,
            laborCategoryId: row.laborCategoryId,
            workUomId: row.workUomId,
            phaseId,
            activityId,
          },
        );
        if (cancelled) return;
        if (response?.status) {
          setRows(response.data?.rows || []);
        } else {
          message.error(
            response?.message || "Failed to load the manpower task breakdown",
          );
        }
      } catch (err) {
        console.error("Error loading manpower task breakdown:", err);
        if (!cancelled)
          message.error("Failed to load the manpower task breakdown");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, row, phaseId, activityId]);

  // Flat rows → phase → activity → task tree, ranked by cost at every level.
  const phaseRows = useMemo<PhaseRow[]>(() => {
    const phaseMap = new Map<
      number,
      {
        name: string;
        activities: Map<number, { name: string; tasks: BreakdownRow[] }>;
      }
    >();
    rows.forEach((r) => {
      if (!phaseMap.has(r.phaseId)) {
        phaseMap.set(r.phaseId, { name: r.phaseName, activities: new Map() });
      }
      const phase = phaseMap.get(r.phaseId)!;
      if (!phase.activities.has(r.activityId)) {
        phase.activities.set(r.activityId, {
          name: r.activityName,
          tasks: [],
        });
      }
      phase.activities.get(r.activityId)!.tasks.push(r);
    });

    const phases = Array.from(phaseMap.entries()).map(([phaseId, phase]) => {
      const activities = Array.from(phase.activities.entries()).map(
        ([activityId, activity]) => {
          const tasks = activity.tasks
            .map((t) => ({
              taskId: t.taskId,
              name: t.taskName,
              sequenceId: t.taskSequenceId,
              qty: Number(t.totalQuantity) || 0,
              cost: Number(t.totalCost) || 0,
            }))
            .sort((a, b) => b.cost - a.cost);
          const cost = tasks.reduce((s, t) => s + t.cost, 0);
          const qty = tasks.reduce((s, t) => s + t.qty, 0);
          return { activityId, name: activity.name, cost, qty, tasks };
        },
      );
      const cost = activities.reduce((s, a) => s + a.cost, 0);
      const qty = activities.reduce((s, a) => s + a.qty, 0);
      return { phaseId, name: phase.name, cost, qty, activities };
    });

    const itemTotal = phases.reduce((s, p) => s + p.cost, 0);
    return phases
      .sort((a, b) => b.cost - a.cost)
      .map((p, pIndex) => ({
        key: p.phaseId,
        rank: pIndex + 1,
        name: p.name,
        qty: p.qty,
        cost: p.cost,
        share: itemTotal > 0 ? (p.cost / itemTotal) * 100 : 0,
        activities: p.activities
          .sort((a, b) => b.cost - a.cost)
          .map((a, aIndex) => ({
            key: a.activityId,
            rank: aIndex + 1,
            name: a.name,
            qty: a.qty,
            cost: a.cost,
            share: p.cost > 0 ? (a.cost / p.cost) * 100 : 0,
            tasks: a.tasks.map((t, tIndex) => ({
              key: t.taskId,
              rank: tIndex + 1,
              name: t.name,
              sequenceId: t.sequenceId,
              qty: t.qty,
              cost: t.cost,
              share: a.cost > 0 ? (t.cost / a.cost) * 100 : 0,
            })),
          })),
      }));
  }, [rows]);

  const itemTotal = useMemo(
    () => phaseRows.reduce((s, p) => s + p.cost, 0),
    [phaseRows],
  );

  const qtyTitle = row?.uomName ? `Qty (${row.uomName})` : "Qty";

  const taskColumns: ColumnsType<TaskRow> = [
    { title: "#", dataIndex: "rank", width: 48, render: rankCell },
    {
      title: "Task",
      dataIndex: "name",
      render: (name: string, r) => (
        <>
          {r.sequenceId ? (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{r.sequenceId}</div>
          ) : null}
          <span style={{ fontWeight: 500 }}>{name}</span>
        </>
      ),
    },
    {
      title: qtyTitle,
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    { title: "Cost", dataIndex: "cost", width: 120, align: "right", render: costCell },
    { title: "% of Activity", dataIndex: "share", width: 150, render: shareCell },
  ];

  const activityColumns: ColumnsType<ActivityRow> = [
    { title: "#", dataIndex: "rank", width: 48, render: rankCell },
    {
      title: "Activity",
      dataIndex: "name",
      render: (name: string, r) => (
        <>
          <div style={{ fontWeight: 500 }} title={name}>{wbsName(name)}</div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            {r.tasks.length} {r.tasks.length === 1 ? "task" : "tasks"}
          </div>
        </>
      ),
    },
    {
      title: qtyTitle,
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    { title: "Cost", dataIndex: "cost", width: 120, align: "right", render: costCell },
    { title: "% of Phase", dataIndex: "share", width: 150, render: shareCell },
  ];

  const phaseColumns: ColumnsType<PhaseRow> = [
    { title: "#", dataIndex: "rank", width: 48, render: rankCell },
    {
      title: "Phase",
      dataIndex: "name",
      render: (name: string, r) => (
        <>
          <div style={{ fontWeight: 500 }} title={name}>{wbsName(name)}</div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            {r.activities.length}{" "}
            {r.activities.length === 1 ? "activity" : "activities"}
          </div>
        </>
      ),
    },
    {
      title: qtyTitle,
      dataIndex: "qty",
      width: 110,
      align: "right",
      render: qtyCell,
    },
    { title: "Cost", dataIndex: "cost", width: 120, align: "right", render: costCell },
    { title: "% of Item", dataIndex: "share", width: 150, render: shareCell },
  ];

  return (
    <Drawer
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {row?.itemName ? `${row.itemName} — Task Breakdown` : "Task Breakdown"}
          {row ? (
            row.source === "PRW" ? (
              <Tag color="blue" style={{ marginRight: 0 }}>
                PRW
              </Tag>
            ) : (
              <Tag color="orange" style={{ marginRight: 0 }}>
                Others
              </Tag>
            )
          ) : null}
        </span>
      }
      placement="right"
      width="min(900px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      {...(getContainer ? { getContainer } : {})}
      footer={
        phaseRows.length > 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
              color: "#022c57",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>Item Total</span>
            <span>{fmtINR(itemTotal)}</span>
          </div>
        ) : null
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin />
        </div>
      ) : phaseRows.length === 0 ? (
        <Empty description="No task usage found for this labour item" />
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <Tag color="blue" style={{ fontSize: 12 }}>
              {rows.length} task{rows.length === 1 ? "" : "s"} across{" "}
              {phaseRows.length} phase{phaseRows.length === 1 ? "" : "s"}
            </Tag>
          </div>
          <Table<PhaseRow>
            className="customTable"
            rowKey="key"
            columns={phaseColumns}
            dataSource={phaseRows}
            pagination={false}
            bordered
            size="small"
            expandable={{
              rowExpandable: (record) => record.activities.length > 0,
              expandedRowRender: (record) => (
                <Table<ActivityRow>
                  className="customTable"
                  rowKey="key"
                  columns={activityColumns}
                  dataSource={record.activities}
                  pagination={false}
                  bordered
                  size="small"
                  expandable={{
                    rowExpandable: (activity) => activity.tasks.length > 0,
                    expandedRowRender: (activity) => (
                      <Table<TaskRow>
                        className="customTable"
                        rowKey="key"
                        columns={taskColumns}
                        dataSource={activity.tasks}
                        pagination={false}
                        bordered
                        size="small"
                      />
                    ),
                  }}
                />
              ),
            }}
          />
        </>
      )}
    </Drawer>
  );
};

export default ManpowerTaskBreakdownDrawer;
